import type { CellContext, SortingState } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import type { AnchorProductRow } from '../../model/types'

import React, { useMemo, useState } from 'react'
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { Share2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { ColumnDataType } from '@/shared/components/data-grid/types'
import { DataTable } from '@/shared/components/tables'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'

export default function AnchorProductTypeTable({
	rows,
}: {
	rows: AnchorProductRow[]
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'is_anchor',
			desc: true,
		},
	])
	const columns = useMemo(() => getColumns(t), [])
	const table = useReactTable<AnchorProductRow>({
		data: rows,
		columns,
		state: {
			sorting,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})

	return (
		<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid'>
			<DataTable
				compact
				getRowClassName={(row) =>
					row?.original.is_current
						? 'h-9 bg-primary-50 dark:bg-primary-950'
						: 'h-9'
				}
				table={table}
			/>
		</div>
	)
}
const columnHelper = createColumnHelper<AnchorProductRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('sku', {
		size: 120,
		header: () => t('sku'),
		cell: (info) => <SkuCell info={info} />,
	}),
	columnHelper.accessor('add_idx', {
		size: 65,
		header: () => t('add_idx', { defaultValue: 'Add idx' }),
		cell: (info) => (
			<NumberField
				value={info.getValue()}
				schema={{ type: ColumnDataType.Number }}
			/>
		),
	}),
	columnHelper.accessor('mul_idx', {
		size: 60,
		header: () => t('mul_idx', { defaultValue: 'Mul idx' }),
		cell: (info) => (
			<NumberField
				value={info.getValue()}
				schema={{ type: ColumnDataType.Number }}
			/>
		),
	}),
]

function SkuCell({ info }: { info: CellContext<AnchorProductRow, string> }) {
	const [, setQuery] = useLocationQuery()

	return (
		<span className='flex items-center space-x-1'>
			<Tooltip>
				<TooltipTrigger asChild>
					<LinkButton
						variant='brand'
						className='truncate'
						onClick={() => {
							setQuery({
								selected: `${info.row.original.sales_entity_id}__${info.row.original.sku}__${info.row.original.optimization_group_id}`,
							})
						}}
					>
						{info.row.original?.is_anchor && <Share2Icon className='size-4' />}
						{info.getValue()}
					</LinkButton>
				</TooltipTrigger>
				<TooltipContent>
					<>
						{info.row.original.title}
						<br />
						{info.getValue()}
					</>
				</TooltipContent>
			</Tooltip>
		</span>
	)
}
